"use client";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import Button from "../components/Button";
import { useEffect, useState } from "react";
import chatService from "../services/chat";

const ChatDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const chat_name = queryParams.get("name");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    async function fetchUserProfile() {
      const payload = { chat_name: chat_name };
      const profile = await chatService.fetchProfiles(payload);
      setProfile(profile.profiles[0]);
    }
    fetchUserProfile();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleChat = () => {
    // Navigate to makeyaarichat
    //localStorage.setItem("chatUserId", profile.userid);
    navigate("/makeyaarichat");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Layout */}
      <div className="md:hidden min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 pb-2">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Dil Say's Profile</h1>
        </div>

        {/* Profile Image Section */}
        {profile && (
          <div className="relative mx-4 mb-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-gray-100">
              <img
                src={profile.profile_picture || "/placeholder.svg"}
                alt={`${profile.chat_name} profile`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h2 className="text-2xl font-bold mb-2">
                  {profile.chat_name} ,{profile.age}
                </h2>
                <div className="flex items-center gap-1 text-sm mb-3">
                  <MapPin size={14} />
                  <span>{profile.distance}</span>
                </div>
                <div className="bg-gray-200/90 backdrop-blur-sm rounded-full px-3 py-2 flex items-center gap-2 text-sm text-gray-800 font-medium inline-flex">
                  <span className="text-base">☕</span>
                  <span>{profile.activity}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat Button */}
        <div className="p-4">
          <Button size="lg" onClick={handleChat} className="w-full">
            Go Back
          </Button>
        </div>
      </div>

      {/* Desktop Layout */}
      {profile && (
        <div className="hidden md:block min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 bg-white rounded-2xl p-6 shadow-sm">
              <button
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Dil Say's Profile
              </h1>
            </div>

            <div className="grid grid-cols-12 gap-8">
              {/* Left Column - Profile Image */}
              <div className="col-span-5">
                <div className="sticky top-8">
                  <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-white shadow-xl">
                    <img
                      src={profile.profile_picture || "/placeholder.svg"}
                      alt={`${profile.chat_name} profile`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                      <h2 className="text-4xl font-bold mb-4">
                        {profile.chat_name}{" "}
                        <span className="text-3xl font-normal">
                          ,{profile.age}
                        </span>
                      </h2>
                      <div className="flex items-center gap-2 text-lg mb-6">
                        <MapPin size={20} />
                        <span>{profile.distance}</span>
                      </div>
                      <div className="bg-white/20 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center gap-3 text-white font-medium inline-flex border border-white/20">
                        <span className="text-2xl">☕</span>
                        <span className="text-lg">{profile.activity}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Content */}
              <div className="col-span-7">
                <div className="space-y-8">
                  {/* Bio Section */}

                  {/* Interests Section */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-2 h-8 bg-purple-600 rounded-full" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Interests
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {profile &&
                        profile.interests &&
                        profile.interests.map(interest => (
                          <div
                            key={interest.id}
                            className="flex items-center gap-4 px-6 py-4 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-purple-50 hover:border-purple-200 transition-all duration-200 group"
                          >
                            <span className="text-2xl group-hover:scale-110 transition-transform duration-200">
                              {interest.emoji}
                            </span>
                            <span className="font-medium text-gray-700 group-hover:text-purple-700 transition-colors">
                              {interest.title}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Action Section */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          Ready to connect?
                        </h4>
                        <p className="text-gray-600">
                          Start a conversation with {profile.name}
                        </p>
                      </div>
                      <div className="flex gap-4">
                        <button className="p-4 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors group">
                          <svg
                            className="w-6 h-6 text-gray-600 group-hover:text-red-500 transition-colors"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <Button
                          size="lg"
                          onClick={handleChat}
                          className="px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                        >
                          Go Back
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Additional Info Cards */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                          <MapPin size={20} className="text-white" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-purple-900">
                            Distance
                          </h5>
                          <p className="text-purple-700">
                            {profile.distance} away
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-lg">☕</span>
                        </div>
                        <div>
                          <h5 className="font-semibold text-green-900">
                            Activity
                          </h5>
                          <p className="text-green-700">{profile.activity}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDetail;
